import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import toNumber from 'lodash/toNumber'

import { breakpoints, colors } from '@/styles'
import { formatDate, isTodayOrPast } from '@/utils/date'
import toBoolean from '@/utils/to-boolean'
import CurrencyFormatter from '@/components/currency-formatter'
import ListingSection from '@/components/listing-section'
import { ParagraphRegular } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`

const InnerWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  background: ${colors.background};
  border-radius: 8px;
  padding: 14px 24px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`

const DetailItem = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 20px 0;
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    border-bottom: solid 1px #eee;
  }

  &:nth-last-child(1),
  &:nth-last-child(2):nth-child(odd) {
    &:after {
      border-bottom: 0;
    }
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    &:nth-last-child(2):nth-child(odd) {
      &:after {
        border-bottom: solid 1px #eee;
      }
    }
  }
`

const DetailText = styled(ParagraphRegular)`
  color: ${colors.grey32};
  font-weight: 500;
`

const DetailTextBold = styled(DetailText)`
  color: ${colors.black};
  font-weight: 600;
`

const formatPrice = (price, locale) => (
  <CurrencyFormatter value={price} locale={locale} />
)

const LeaseDetails = ({ listings, unit, className }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const locale = router?.locale
  const LEASE_MAP = {
    fixed: t('common.fixed_term', 'Fixed Term'),
    month_to_month: t('common.month_to_month', 'Month to Month'),
    other: t('common.other', 'Other')
  }

  const renderAvailability = date => {
    const availableNow = isTodayOrPast(date)
    return availableNow
      ? t('listing:b.listing.view.available_now.text', 'Available Now')
      : formatDate(date, 'MMM d, yyyy', router.locale)
  }

  const getAdditionalFeatures = features => {
    const featureData = {
      parking: t('listing:b.listing.view.parking_available.text', 'Parking'),
      storage: t('listing:b.listing.view.storage.available.text', 'Storage')
    }
    const formatFee = (fee, frequency) => {
      if (frequency === 'free') {
        return (
          <DetailTextBold>
            {t('listing:b.listing.view.included.text', 'Included')}
          </DetailTextBold>
        )
      }
      return (
        <DetailTextBold>
          {formatPrice(fee, locale)} /{' '}
          {t(`b.listing.view.price_frequency_${frequency}.text`)}
        </DetailTextBold>
      )
    }

    return features.map(feature => {
      const featureId = feature.fee_txt_id
      if (feature.fee_frequency_txt_id === 'not_available') return null
      return (
        <DetailItem key={featureId}>
          <DetailText>{featureData[featureId]}</DetailText>
          {formatFee(feature.fee, feature.fee_frequency_txt_id)}
        </DetailItem>
      )
    })
  }

  const formatLeaseType = value => LEASE_MAP[value]

  const minLeaseLength = (value, measure) => {
    const count = toNumber(value)
    if (measure === 'no_minimum' || value === '0') {
      return t('listing:b.listing.view.lease_length.text', 'No Minimum')
    } else if (measure === 'days') {
      return t('listing:b.listing.view.lease_length_day.text', {
        defaultValue: '{{count}} day',
        defaultValue_plural: '{{count}} days',
        count
      })
    } else if (measure === 'weeks') {
      return t('listing:b.listing.view.lease_length_week.text', {
        defaultValue: '{{count}} week',
        defaultValue_plural: '{{count}} weeks',
        count
      })
    } else if (measure === 'months') {
      return t('listing:b.listing.view.lease_length_month.text', {
        defaultValue: '{{count}} month',
        defaultValue_plural: '{{count}} months',
        count
      })
    } else if (measure === 'years') {
      return t('listing:b.listing.view.lease_length_year.text', {
        defaultValue: '{{count}} year',
        defaultValue_plural: '{{count}} years',
        count
      })
    }
    return ''
  }

  const formatMaxOccupancy = value =>
    t('listing:b.listing.view.max_occupancy_people.text', {
      defaultValue: '{{count}} person',
      defaultValue_plural: '{{count}} people',
      count: value
    })

  return (
    <Wrapper className={className}>
      <ListingSection
        title={t('listing:b.listing.view.lease_details.text', 'Lease Details')}
        customGap="0px"
      >
        <InnerWrapper>
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.move_in.text', 'Move-in Date')}
            </DetailText>
            <DetailTextBold>
              {renderAvailability(listings.availability_date)}
            </DetailTextBold>
          </DetailItem>
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.lease_type.text', 'Lease Type')}
            </DetailText>
            <DetailTextBold>
              {formatLeaseType(listings.lease_type)}
            </DetailTextBold>
          </DetailItem>

          {toBoolean(listings.max_occupancy) && (
            <DetailItem>
              <DetailText>
                {t(
                  'listing:b.listing.view.max_occupancy.text',
                  'Max. Occupancy'
                )}
              </DetailText>
              <DetailTextBold>
                {formatMaxOccupancy(toNumber(listings.max_occupancy))}
              </DetailTextBold>
            </DetailItem>
          )}
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.minimum_lease.title', 'Minimum Lease')}
            </DetailText>
            <DetailTextBold>
              {toBoolean(
                minLeaseLength(
                  listings.min_lease_period,
                  listings.min_lease_period_type
                )
              )
                ? minLeaseLength(
                    listings.min_lease_period,
                    listings.min_lease_period_type
                  )
                : t(
                    'listing:b.listing.view.minimum_lease.default',
                    'No Minimum'
                  )}
            </DetailTextBold>
          </DetailItem>
          {getAdditionalFeatures(listings.listing_fees)}
          {toBoolean(unit.count_dens) && (
            <DetailItem>
              <DetailText>
                {t('listing:b.listing.view.den_flex_included.text', 'Den/Flex')}
              </DetailText>
              <DetailTextBold>{unit.count_dens}</DetailTextBold>
            </DetailItem>
          )}
        </InnerWrapper>
      </ListingSection>
    </Wrapper>
  )
}

export default LeaseDetails
